import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import api from '../services/api';
import {
  BuildingStorefrontIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';

/* eslint-disable @typescript-eslint/no-explicit-any */

interface ClubsData {
  glob: {
    total: number; clubs: number; avg_csat: number | null;
    responded: number; requested: number; open: number;
    automatable: number; automatable_pct: number;
    csat_dist: Record<string, number>;
    status_dist: Record<string, number>;
    topics: { name: string; count: number; auto: boolean }[];
  };
  clubs: any[];
}

const TOPIC_RECS: Record<string, string> = {
  'Создание аккаунта': 'Авто-создание через бота (/newacc) или Gizmo API.',
  'Сброс пароля / вход': 'Самообслуживание: /resetpass или интеграция с Gizmo.',
  'Пакеты / оплата / возврат': 'KB «депозит vs пакет», ускоренный SLA на возвраты.',
  'Обновление / установка игр': 'Ночное авто-обновление по расписанию + KB.',
  'Faceit / античит': 'KB + проактивный фикс, а не разовые тикеты.',
  'После света / проверка ПК': 'Чек-лист «после отключения электричества».',
};

const sevColor = (s: string) =>
  s === 'crit' ? 'text-rose-500' : s === 'warn' ? 'text-amber-500' : 'text-emerald-500';
const sevBar = (s: string) =>
  s === 'crit' ? 'bg-rose-500' : s === 'warn' ? 'bg-amber-500' : 'bg-emerald-500';

const ClubsReport: React.FC = () => {
  const [data, setData] = useState<ClubsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('all');
  const [sel, setSel] = useState<string>('');

  useEffect(() => {
    setLoading(true);
    api.get('/api/reports/clubs', { params: { period } })
      .then((res) => {
        const d = res.data?.data;
        setData(d);
        if (d?.clubs?.length) setSel(d.clubs[0].cid);
      })
      .catch((e: any) => toast.error(e?.response?.data?.message || 'Ошибка загрузки отчёта'))
      .finally(() => setLoading(false));
  }, [period]);

  const club = useMemo(
    () => data?.clubs.find((c) => c.cid === sel) || null,
    [data, sel],
  );

  if (loading) return <div className="text-center py-12 text-muted-foreground">Строю отчёт по клубам…</div>;
  if (!data || !data.glob.total) return <div className="text-center py-12 text-muted-foreground">Нет данных.</div>;

  const g = data.glob;
  const attn = data.clubs.filter((c) => c.severity !== 'ok');
  const maxTopic = Math.max(1, ...g.topics.map((t) => t.count));

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-muted-foreground">
          {g.total} заявок · {g.clubs} клубов · CSAT {g.avg_csat} · отклик {Math.round(g.responded / g.requested * 100)}%
        </p>
        <select
          value={period}
          onChange={(e) => setPeriod(e.target.value)}
          className="h-9 px-3 rounded-md border border-border bg-card text-sm text-foreground"
        >
          <option value="all">За всё время</option>
          <option value="90d">90 дней</option>
          <option value="30d">30 дней</option>
          <option value="7d">7 дней</option>
        </select>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          ['Заявок', g.total],
          ['Клубов', g.clubs],
          ['Средний CSAT', g.avg_csat != null ? g.avg_csat + ' ★' : '—'],
          ['Автоматизируемо', g.automatable_pct + '%'],
          ['Открытых', g.open],
        ].map(([l, v]) => (
          <div key={l as string} className="bg-card border border-border rounded-lg p-3">
            <div className="text-2xl font-bold text-foreground">{v}</div>
            <div className="text-xs text-muted-foreground">{l}</div>
          </div>
        ))}
      </div>

      {/* Global topics */}
      <div className="bg-card border border-border rounded-lg p-4">
        <h3 className="text-sm font-semibold text-foreground mb-3">Темы обращений по всей сети</h3>
        <div className="space-y-1.5">
          {g.topics.map((t) => (
            <div key={t.name} className="flex items-center gap-2 text-xs">
              <span className="w-52 shrink-0 text-foreground/90 truncate">
                {t.name}{t.auto && <span className="text-primary"> ⚙</span>}
              </span>
              <div className="flex-1 h-3 bg-muted rounded-full overflow-hidden">
                <div className={`h-full rounded-full ${t.auto ? 'bg-primary' : 'bg-sky-500/60'}`}
                     style={{ width: `${(t.count / maxTopic) * 100}%` }} />
              </div>
              <span className="w-16 text-right text-muted-foreground">
                {t.count} ({Math.round(t.count / g.total * 100)}%)
              </span>
            </div>
          ))}
        </div>
        <p className="text-xs text-muted-foreground mt-2">⚙ — автоматизируемая рутина (всего {g.automatable_pct}% потока)</p>
      </div>

      {/* Drilldown */}
      <div className="bg-card border border-border rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <BuildingStorefrontIcon className="h-5 w-5 text-primary" />
          <h3 className="text-sm font-semibold text-foreground">Разбор по клубу</h3>
          <div className="relative">
            <select
              value={sel}
              onChange={(e) => setSel(e.target.value)}
              className="h-9 pl-3 pr-8 rounded-md border border-border bg-background text-sm text-foreground appearance-none min-w-[260px]"
            >
              {data.clubs.map((c) => (
                <option key={c.cid} value={c.cid}>{c.name} ({c.total})</option>
              ))}
            </select>
            <ChevronDownIcon className="h-4 w-4 absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          </div>
        </div>

        {club && <ClubDrill club={club} />}
      </div>

      {/* Attention */}
      {attn.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-foreground mb-2">Требуют внимания ({attn.length})</h3>
          <div className="space-y-2">
            {attn.map((c) => (
              <button
                key={c.cid}
                onClick={() => { setSel(c.cid); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                className={`w-full text-left bg-card border border-border rounded-lg p-3 hover:bg-accent transition-colors border-l-4 ${
                  c.severity === 'crit' ? 'border-l-rose-500' : 'border-l-amber-500'
                }`}
              >
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <span className="font-medium text-sm text-foreground">{c.name}</span>
                  <span className="text-xs text-muted-foreground">
                    {c.total} заявок · CSAT <b className={sevColor(c.severity)}>{c.avg_csat ?? '—'}</b>
                    {c.low12 ? ` · ${c.low12}×(1–2★)` : ''} · реш. {c.avg_res_h ?? '—'}ч
                  </span>
                </div>
                <ul className="mt-1 space-y-0.5">
                  {c.recs.map((r: string, i: number) => (
                    <li key={i} className="text-xs text-foreground/80">{r}</li>
                  ))}
                </ul>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Full table */}
      <div className="bg-card border border-border rounded-lg p-4">
        <h3 className="text-sm font-semibold text-foreground mb-3">Все клубы</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-muted-foreground text-xs">
                <th className="text-left py-2 px-2">Клуб</th>
                <th className="text-right px-2">Заявок</th>
                <th className="text-right px-2">Откр.</th>
                <th className="text-right px-2">CSAT</th>
                <th className="text-right px-2">1–2★</th>
                <th className="text-right px-2">Реш.,ч</th>
                <th className="text-right px-2">Отклик</th>
              </tr>
            </thead>
            <tbody>
              {data.clubs.map((c) => (
                <tr key={c.cid}
                    onClick={() => { setSel(c.cid); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                    className="border-t border-border hover:bg-accent cursor-pointer">
                  <td className="py-2 px-2 text-foreground">
                    <span className={`inline-block w-2 h-2 rounded-full mr-2 ${sevBar(c.severity)}`} />
                    {c.name}
                  </td>
                  <td className="text-right px-2">{c.total}</td>
                  <td className="text-right px-2">{c.open || ''}</td>
                  <td className="text-right px-2">{c.avg_csat ?? '—'}</td>
                  <td className="text-right px-2 text-rose-500">{c.low12 || ''}</td>
                  <td className="text-right px-2">{c.avg_res_h ?? '—'}</td>
                  <td className="text-right px-2">{c.response_rate == null ? '—' : Math.round(c.response_rate * 100) + '%'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const ClubDrill: React.FC<{ club: any }> = ({ club }) => {
  const max = Math.max(1, ...club.topic_list.map((t: any) => t.count));
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        {[
          ['Заявок', club.total],
          ['Открыто', club.open],
          ['CSAT', club.avg_csat != null ? club.avg_csat + '★' : '—'],
          ['Реш.,ч', club.avg_res_h ?? '—'],
          ['Эскалаций', club.escalated],
        ].map(([l, v]) => (
          <div key={l as string} className="border border-border rounded-md p-2">
            <div className="text-lg font-bold text-foreground">{v}</div>
            <div className="text-[11px] text-muted-foreground">{l}</div>
          </div>
        ))}
      </div>

      <div>
        <h4 className="text-xs font-semibold text-foreground mb-2">Частые темы</h4>
        <div className="space-y-2">
          {club.topic_list.map((t: any) => {
            const pct = Math.round(t.count / club.total * 100);
            const examples = club.examples[t.name] || [];
            return (
              <div key={t.name}>
                <div className="flex items-center gap-2 text-xs">
                  <span className="w-52 shrink-0 text-foreground/90 truncate">{t.name}</span>
                  <div className="flex-1 h-4 bg-muted rounded-full overflow-hidden">
                    <div className="h-full rounded-full bg-primary flex items-center justify-end pr-1.5 text-[10px] text-white"
                         style={{ width: `${Math.max(8, (t.count / max) * 100)}%` }}>
                      {t.count}
                    </div>
                  </div>
                  <span className="w-12 text-right text-muted-foreground">{pct}%</span>
                </div>
                {TOPIC_RECS[t.name] && (
                  <div className="ml-52 text-[11px] text-primary/90 mt-0.5">💡 {TOPIC_RECS[t.name]}</div>
                )}
                {examples.length > 0 && (
                  <details className="ml-52 mt-0.5">
                    <summary className="text-[11px] text-muted-foreground cursor-pointer">примеры ({examples.length})</summary>
                    <div className="mt-1 space-y-0.5">
                      {examples.map((e: any) => (
                        <div key={e.tn} className="text-[11px] text-muted-foreground">
                          #{e.tn}{e.r ? ` ${e.r}★` : ''}: {e.txt}
                        </div>
                      ))}
                    </div>
                  </details>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {club.negatives?.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-rose-400 mb-2">Негативные оценки ({club.negatives.length})</h4>
          <div className="space-y-1.5">
            {club.negatives.map((n: any) => (
              <div key={n.tn} className="text-xs border-l-2 border-rose-500 pl-2 text-muted-foreground">
                <b className="text-rose-400">{n.r}★</b> #{n.tn} — «{n.problem}»
                {n.comment && <> · отзыв: «{n.comment}»</>}
                {n.resolution && <> · решение: «{n.resolution}»</>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ClubsReport;
